"use client";

import {
  Container,
  Title,
  Text,
  Stack,
  Box,
  Card,
  Group,
  Badge,
  SimpleGrid,
  Paper,
  Button,
  Flex,
  ThemeIcon,
  List,
} from "@mantine/core";
import {
  IconPalette,
  IconArrowRight,
  IconEye,
  IconSparkles,
  IconX,
} from "@tabler/icons-react";
import { Link } from "@/i18n/navigation";
import { useTranslations } from "next-intl";
import { colorSeasons } from "@/constants";

export default function TrueSummerVsSoftSummerContent() {
  const t = useTranslations("trueSummerVsSoftSummer");

  const trueSummerData = colorSeasons.SUMMER.subseasons.TRUE;
  const softSummerData = colorSeasons.SUMMER.subseasons.COOL; // COOL maps to Soft Summer in display

  const comparisonPoints = [
    {
      aspect: "Color Clarity",
      trueSummer: "Clear, balanced cool colors with medium saturation",
      softSummer: "Muted, gentle colors with low saturation and subtle warmth",
      icon: IconPalette,
    },
    {
      aspect: "Undertone",
      trueSummer: "Purely cool undertones throughout",
      softSummer: "Cool undertones with a hint of warmth (neutral-cool)",
      icon: IconEye,
    },
    {
      aspect: "Best For",
      trueSummer: "Clear, balanced coloring with cool features",
      softSummer: "Delicate, ethereal coloring with muted features",
      icon: IconSparkles,
    },
    {
      aspect: "Avoid",
      trueSummer: "Warm, golden tones and overly muted colors",
      softSummer: "Bright, saturated colors and harsh contrasts",
      icon: IconX,
    },
  ];

  return (
    <Container size="lg" py="xl">
      <Stack gap="xl">
        {/* Hero Section */}
        <Box ta="center">
          <Title order={1} size="h1" mb="md">
            True Summer vs Soft Summer
          </Title>
          <Text size="lg" c="dimmed" maw={600} mx="auto" mb="xl">
            Both are cool summer subtypes, but True Summer has clearer colors while Soft Summer is more muted.
            Learn which one matches your natural coloring.
          </Text>
        </Box>

        {/* Quick Comparison Overview */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="md" ta="center">
            Quick Comparison
          </Title>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
            <Paper withBorder p="lg" radius="md">
              <Stack gap="md" ta="center">
                <Text size="xl">{trueSummerData.emoji}</Text>
                <Title order={3}>True Summer</Title>
                <Text size="sm" c="dimmed">
                  Cool and clear. Medium contrast with balanced, refreshing colors.
                </Text>
                <Badge color="blue" variant="light">
                  Cool + Clear + Balanced
                </Badge>
                <Group gap="xs" justify="center">
                  {trueSummerData.primaryColors.slice(0, 5).map((color, i) => (
                    <Box key={i} w={24} h={24} style={{ background: color.hex, borderRadius: 4 }} />
                  ))}
                </Group>
              </Stack>
            </Paper>
            <Paper withBorder p="lg" radius="md">
              <Stack gap="md" ta="center">
                <Text size="xl">{softSummerData.emoji}</Text>
                <Title order={3}>Soft Summer</Title>
                <Text size="sm" c="dimmed">
                  Cool and muted. Low contrast with gentle, dusty colors.
                </Text>
                <Badge color="blue" variant="light">
                  Cool + Muted + Gentle
                </Badge>
                <Group gap="xs" justify="center">
                  {softSummerData.primaryColors.slice(0, 5).map((color, i) => (
                    <Box key={i} w={24} h={24} style={{ background: color.hex, borderRadius: 4 }} />
                  ))}
                </Group>
              </Stack>
            </Paper>
          </SimpleGrid>
        </Card>

        {/* Key Differences */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="lg" ta="center">
            Key Differences
          </Title>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
            {comparisonPoints.map((point, index) => (
              <Paper key={index} withBorder p="lg" radius="md">
                <Stack gap="md">
                  <Group>
                    <ThemeIcon color="blue" variant="light" size="lg">
                      <point.icon size={20} />
                    </ThemeIcon>
                    <Text fw={600} size="lg">
                      {point.aspect}
                    </Text>
                  </Group>
                  <Box>
                    <Text fw={500} c="blue" size="sm" mb="xs">
                      True Summer:
                    </Text>
                    <Text size="sm" mb="md">
                      {point.trueSummer}
                    </Text>
                    <Text fw={500} c="blue" size="sm" mb="xs">
                      Soft Summer:
                    </Text>
                    <Text size="sm">{point.softSummer}</Text>
                  </Box>
                </Stack>
              </Paper>
            ))}
          </SimpleGrid>
        </Card>

        {/* Color Palette Comparison */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="lg" ta="center">
            Color Palette Comparison
          </Title>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="xl">
            <Box>
              <Title order={3} mb="md" ta="center">True Summer Colors</Title>
              <Text size="sm" c="dimmed" ta="center" mb="md">Clear, balanced cool tones</Text>
              <SimpleGrid cols={4} spacing="xs">
                {trueSummerData.primaryColors.slice(0, 8).map((color, index) => (
                  <Box key={index}>
                    <Box
                      style={{
                        backgroundColor: color.hex,
                        height: 40,
                        borderRadius: 4,
                        border: "1px solid #e0e0e0",
                      }}
                      title={`${color.name} - ${color.hex}`}
                    />
                    <Text size="xs" ta="center" c="dimmed" mt={4}>{color.hex}</Text>
                  </Box>
                ))}
              </SimpleGrid>
            </Box>
            <Box>
              <Title order={3} mb="md" ta="center">Soft Summer Colors</Title>
              <Text size="sm" c="dimmed" ta="center" mb="md">Muted, gentle cool tones</Text>
              <SimpleGrid cols={4} spacing="xs">
                {softSummerData.primaryColors.slice(0, 8).map((color, index) => (
                  <Box key={index}>
                    <Box
                      style={{
                        backgroundColor: color.hex,
                        height: 40,
                        borderRadius: 4,
                        border: "1px solid #e0e0e0",
                      }}
                      title={`${color.name} - ${color.hex}`}
                    />
                    <Text size="xs" ta="center" c="dimmed" mt={4}>{color.hex}</Text>
                  </Box>
                ))}
              </SimpleGrid>
            </Box>
          </SimpleGrid>
        </Card>

        {/* How to Tell at Home */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="lg" ta="center">
            How to Tell the Difference at Home
          </Title>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
            <Box>
              <Title order={3} size="h4" mb="md">The Saturation Test</Title>
              <Text size="sm" c="dimmed" mb="sm">
                Compare a clear periwinkle blue vs a dusty lavender near your face:
              </Text>
              <List size="sm" spacing="xs">
                <List.Item><strong>True Summer:</strong> The clear periwinkle brightens your face</List.Item>
                <List.Item><strong>Soft Summer:</strong> The dusty lavender feels more harmonious</List.Item>
              </List>
            </Box>
            <Box>
              <Title order={3} size="h4" mb="md">The Contrast Test</Title>
              <Text size="sm" c="dimmed" mb="sm">
                Look at your natural features (hair, skin, eyes):
              </Text>
              <List size="sm" spacing="xs">
                <List.Item><strong>True Summer:</strong> Medium contrast between features</List.Item>
                <List.Item><strong>Soft Summer:</strong> Low contrast, features blend softly together</List.Item>
              </List>
            </Box>
          </SimpleGrid>
          <Paper withBorder p="md" radius="md" mt="lg" bg="gray.0">
            <Text size="sm" c="dimmed">
              Still unsure? These tests can be tricky in artificial lighting or with makeup on. A photo analysis removes the guesswork.
            </Text>
          </Paper>
        </Card>

        {/* Final CTA */}
        <Card withBorder p="xl" radius="md" bg="violet.0">
          <Stack gap="lg" ta="center">
            <Title order={2} size="h2">
              Want a Definitive Answer?
            </Title>
            <Text size="lg" c="dimmed" maw={500} mx="auto">
              Upload a photo and get a confident result — no more guessing between True Summer and Soft Summer.
            </Text>
            <Flex gap="md" justify="center" wrap="wrap">
              <Button
                size="lg"
                component={Link}
                href="/ai-color-analysis"
                color="violet"
                rightSection={<IconArrowRight size={20} />}
              >
                Confirm My Season
              </Button>
              <Button
                size="lg"
                variant="outline"
                component={Link}
                href="/color-analysis-quiz"
                color="violet"
              >
                Start With Free Quiz
              </Button>
            </Flex>
            <Group justify="center" gap="xl" mt="md">
              <Button
                variant="subtle"
                component={Link}
                href="/color-palette/summer/true"
                size="sm"
                color="gray"
              >
                View True Summer Palette →
              </Button>
              <Button
                variant="subtle"
                component={Link}
                href="/color-palette/summer/cool"
                size="sm"
                color="gray"
              >
                View Soft Summer Palette →
              </Button>
            </Group>
          </Stack>
        </Card>
      </Stack>
    </Container>
  );
}
